import { DateTime, Duration } from "luxon";

import { PartialBy } from "@app/common/types";

export interface SingleDimensionMeta {
  id: string;
  queryName: string;
  displayName: string;
  toFormat: (value: number | string) => string;
}

export interface MultiDimensionMeta {
  id: string;
  displayName: string;
  getDimensions: () => SingleDimensionMeta[];
  toValue: (data: any) => string;
  toFormat: (value: number | string) => string;
}

const createSingleDimensionMeta = (
  meta: PartialBy<SingleDimensionMeta, "toFormat">
): SingleDimensionMeta => {
  return {
    toFormat: (value) => `${value}`,
    ...meta,
  };
};

const createMultiDimensionMeta = (
  meta: PartialBy<MultiDimensionMeta, "toFormat">
): MultiDimensionMeta => {
  return {
    toFormat: (value) => `${value}`,
    ...meta,
  };
};

const day = createSingleDimensionMeta({
  id: "day",
  queryName: "day",
  displayName: "Day",
});

const dayOfWeek = createSingleDimensionMeta({
  id: "dayOfWeek",
  queryName: "dayOfWeek",
  displayName: "Day of Week",
});

// 3 hr buckets
const timeOfDay = createSingleDimensionMeta({
  id: "timeOfDay",
  queryName: "binnedStartTime",
  displayName: "Time Of Day",
});

// 1 hr buckets
const hour = createSingleDimensionMeta({
  id: "hour",
  queryName: "startTimeHour",
  displayName: "Time Of Day",
});

const meetingName = createSingleDimensionMeta({
  id: "meetingName",
  queryName: "title",
  displayName: "Meeting Name",
});

const meetingSeriesId = createSingleDimensionMeta({
  id: "meetingSeriesId",
  queryName: "recurringEventId",
  displayName: "Meeting Series Id",
});

const calendarObservationEventId = createSingleDimensionMeta({
  id: "calendarObservationEventId",
  queryName: "calendarObservationEventId",
  displayName: "Meeting Id",
});

const observedStartTime = createSingleDimensionMeta({
  id: "observedStartTime",
  queryName: "observedStartTime",
  displayName: "Start Time",
});

const observedDuration = createSingleDimensionMeta({
  id: "observedDuration",
  queryName: "observedDuration",
  displayName: "Meeting Duration",
});

const scheduledStartTime = createSingleDimensionMeta({
  id: "scheduledStartTime",
  queryName: "scheduledStartTime",
  displayName: "Scheduled Start Time",
});

const scheduledDuration = createSingleDimensionMeta({
  id: "scheduledDuration",
  queryName: "scheduledDuration",
  displayName: "Scheduled Duration",
});

const meetingId = createSingleDimensionMeta({
  id: "meetingId",
  queryName: "sessionId",
  displayName: "Meeting Id",
});

const meetingOwnerId = createSingleDimensionMeta({
  id: "meetingOwnerId",
  queryName: "sMeetingOwnerUserId",
  displayName: "Meeting Owner Id",
});

const participantId = createSingleDimensionMeta({
  id: "participantId",
  queryName: "userId",
  displayName: "Participant Id",
});

const participantName = createSingleDimensionMeta({
  id: "participantName",
  queryName: "participantName",
  displayName: "Participant Name",
});

const hasConferenceRoom = createSingleDimensionMeta({
  id: "hasConferenceRoom",
  queryName: "hasConferenceRoom",
  displayName: "Conference Room Detected",
});

const topics = createSingleDimensionMeta({
  id: "topics",
  queryName: "topics",
  displayName: "Topics",
});

const participantCount = createSingleDimensionMeta({
  id: "participantCount",
  queryName: "observedParticipantCount",
  displayName: "# Participants",
});

const scheduledParticipantCount = createSingleDimensionMeta({
  id: "scheduledParticipantCount",
  queryName: "scheduledParticipantCount",
  displayName: "# Scheduled Participants",
});

const sessionLevelTopic = createSingleDimensionMeta({
  id: "sessionLevelTopic",
  queryName: "topic",
  displayName: "Session Level Topic",
});

const participant = createMultiDimensionMeta({
  id: "participant",
  displayName: "Participant",
  getDimensions: () => [participantId, participantName],
  toValue: (data: any) => data[participantName.id],
});

const meetingSeries = createMultiDimensionMeta({
  id: "meetingSeries",
  displayName: "Meeting Series",
  getDimensions: () => [meetingName, meetingSeriesId],
  toValue: (data: any) => data[meetingName.id],
});

const meeting = createMultiDimensionMeta({
  id: "meeting",
  displayName: "Meeting",
  getDimensions: () => [meetingName, meetingId],
  toValue: (data: any) => data[meetingName.id],
});

const observedEndTime = createMultiDimensionMeta({
  id: "observedEndTime",
  displayName: "Observed End Time",
  getDimensions: () => [observedStartTime, observedDuration],
  toValue: (data: any) =>
    DateTime.fromISO(data[observedStartTime.id])
      .plus(Duration.fromMillis(data[observedDuration.id] * 1000))
      .toISO() ?? "N/A",
});

const workspaceId = createSingleDimensionMeta({
  id: "workspaceId",
  queryName: "workspaceId",
  displayName: "Workspace Id",
});

const teamId = createSingleDimensionMeta({
  id: "teamId",
  queryName: "teamId",
  displayName: "Team Id",
});

const meetingPlatform = createSingleDimensionMeta({
  id: "meetingPlatform",
  queryName: "meetingPlatform",
  displayName: "Meeting Platform",
});

export type DimensionMeta = SingleDimensionMeta | MultiDimensionMeta;

const META_BY_ID = {
  day,
  dayOfWeek,
  calendarObservationEventId,
  hour,
  meetingPlatform,
  meetingName,
  meetingSeriesId,
  meetingId,
  meetingOwnerId,
  meetingSeries,
  meeting,
  hasConferenceRoom,
  participant,
  participantCount,
  participantId,
  participantName,
  observedStartTime,
  observedDuration,
  scheduledStartTime,
  scheduledDuration,
  scheduledParticipantCount,
  timeOfDay,
  topics,
  sessionLevelTopic,
  observedEndTime,
  workspaceId,
  teamId,
};

export type QueryDimension = keyof typeof META_BY_ID;

export type QueryStringDimension = Exclude<
  QueryDimension,
  "observedDuration" | "scheduledDuration"
>;

export type QueryNumberDimension = Extract<
  QueryDimension,
  "observedDuration" | "scheduledDuration"
>;

export const getDimensionMetaById = (
  id: QueryDimension | DimensionMeta
): DimensionMeta => {
  if (typeof id !== "string") {
    return id;
  }
  const meta = META_BY_ID[id];
  if (!meta) {
    throw Error(`no dimension by id ${id} found`);
  }
  return meta;
};
